import { useState, useEffect, useCallback, Fragment } from 'react';
import { toast } from 'react-toastify';
import { useConfirm } from 'material-ui-confirm';
import {
  Box,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Tooltip,
} from '@mui/material';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import PendingActionsIcon from '@mui/icons-material/PendingActions';

import fixRequestServices from '../../services/fixRequestServices';
import { moment } from '../../utils/dateUtils';

const STATUS_COLORS = {
  Waiting: 'warning',
  Scheduled: 'info',
  'In Progress': 'secondary',
  Done: 'success',
  Cancelled: 'default',
};

/**
 * "Wait List / Fixes" row content for the proposal editor.
 *
 * - Lists the fix requests already linked to this proposal (as original or
 *   paid contract).
 * - "Create Fix Request" — free rework on this (signed) contract; the request
 *   gets a `<publicId>-F<n>` number and lands in the Wait List.
 * - "Send to Wait List" — for a paid rework contract with no installation
 *   dates: parks it for manager scheduling instead of picking a date.
 */
const WaitListSection = ({ proposal, id, refresh }) => {
  const confirm = useConfirm();

  const [requests, setRequests] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('Medium');
  const [saving, setSaving] = useState(false);

  const isSigned = !!(proposal?.signatureId || proposal?.signature);
  const hasDates = (proposal?.installationSchedule || []).length > 0;
  const inWaitList = !!proposal?.sentToWaitList;

  const loadRequests = useCallback(async () => {
    if (!id) return;
    try {
      const response = await fixRequestServices.getByProposal(id);
      setRequests(response.data || []);
    } catch (err) {
      // Non-blocking: the row simply shows no requests.
      console.error('Failed to load fix requests:', err);
    }
  }, [id]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const handleCreateFix = async () => {
    if (!description.trim()) {
      toast.error('Describe the problem / work to be done');
      return;
    }
    setSaving(true);
    try {
      const response = await fixRequestServices.create({
        proposalId: id,
        issueDescription: description.trim(),
        priority,
      });
      toast.success(`Fix request ${response.data?.requestNumber || ''} sent to Wait List`);
      setCreateOpen(false);
      setDescription('');
      setPriority('Medium');
      loadRequests();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to create fix request');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleSendToWaitList = () => {
    confirm({
      title: 'Send contract to Wait List?',
      description:
        'The contract will be parked for manager scheduling instead of picking an installation date now. ' +
        'Managers will assign the date and resources from the Wait List page.',
      confirmationText: 'Send to Wait List',
    })
      .then(async () => {
        setSaving(true);
        try {
          await fixRequestServices.sendToWaitList(id);
          toast.success('Contract sent to Wait List');
          loadRequests();
          refresh();
        } catch (err) {
          toast.error(err?.response?.data?.message || 'Failed to send to Wait List');
          console.error(err);
        } finally {
          setSaving(false);
        }
      })
      .catch(() => {});
  };

  return (
    <Fragment>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 0.5 }}>
        {inWaitList && (
          <Chip
            icon={<PendingActionsIcon />}
            label="In Wait List — awaiting manager scheduling"
            color="warning"
            size="small"
            sx={{ alignSelf: 'flex-start', fontWeight: 600 }}
          />
        )}

        {requests.map((request) => (
          <Box key={request.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {request.requestNumber}
            </Typography>
            <Chip label={request.type} size="small" variant="outlined" />
            <Chip label={request.status} size="small" color={STATUS_COLORS[request.status] || 'default'} />
            {request.scheduledDate && (
              <Typography variant="caption" color="text.secondary">
                {moment(String(request.scheduledDate).split('T')[0], 'YYYY-MM-DD').format('MM/DD/YYYY')}
                {request.scheduledTime ? ` (${request.scheduledTime})` : ''}
              </Typography>
            )}
          </Box>
        ))}

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Tooltip
            title={isSigned ? '' : 'The proposal must be signed by the customer first'}
            placement="top"
          >
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<BuildCircleIcon />}
                disabled={!isSigned || saving}
                onClick={() => setCreateOpen(true)}
              >
                Create Fix Request
              </Button>
            </span>
          </Tooltip>

          {!hasDates && !inWaitList && (
            <Tooltip
              title={isSigned ? '' : 'The proposal must be signed by the customer first'}
              placement="top"
            >
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  startIcon={<PendingActionsIcon />}
                  disabled={!isSigned || saving}
                  onClick={handleSendToWaitList}
                >
                  Send to Wait List
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Fix Request — {proposal?.publicId}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            A free (no-charge) fix on this contract. The request will get the number{' '}
            <strong>{proposal?.publicId}-F{(requests.filter((r) => r.type === 'Free Fix').length || 0) + 1}</strong>{' '}
            and appear in the Wait List for manager scheduling.
          </Typography>
          <TextField
            label="Problem description / what needs to be done"
            multiline
            minRows={4}
            fullWidth
            required
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Priority"
            select
            fullWidth
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
          >
            <MenuItem value="High">High</MenuItem>
            <MenuItem value="Medium">Medium</MenuItem>
            <MenuItem value="Low">Low</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateFix} disabled={saving}>
            {saving ? 'Sending...' : 'Send to Wait List'}
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default WaitListSection;
